import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeft, TrendingUp, TrendingDown, Target, Zap, Brain, Clock, BarChart3, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CognitiveProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profRes, settRes, recRes] = await Promise.all([
          axios.get(`${API}/adaptive/profile`, { withCredentials: true }),
          axios.get(`${API}/adaptive/settings`, { withCredentials: true }),
          axios.get(`${API}/adaptive/recommend`, { withCredentials: true })
        ]);
        setProfile(profRes.data);
        setSettings(settRes.data);
        setRecommendations(recRes.data.recommendations || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="profile-loading">
        <div className="mono-data text-lg animate-pulse-red">Analyzing Cognitive Data...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="profile-empty">
        <div className="text-center max-w-md">
          <Brain className="w-12 h-12 text-[#09090B] mx-auto mb-6" />
          <h2 className="text-2xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            No Cognitive Data Yet
          </h2>
          <p className="text-zinc-600 mb-8">Complete at least one session to see your cognitive profile.</p>
          <Link to="/programs/prog_memory_free" className="btn-accent" data-testid="start-training-btn">
            Start Training
          </Link>
        </div>
      </div>
    );
  }

  const radarData = [
    { subject: 'Memory', score: profile.memory_score_avg, fullMark: 100 },
    { subject: 'Recall', score: profile.recall_accuracy, fullMark: 100 },
    { subject: 'Sequence', score: profile.sequence_accuracy, fullMark: 100 },
    { subject: 'Speed', score: profile.speed_score, fullMark: 100 },
    { subject: 'Consistency', score: profile.consistency_score, fullMark: 100 }
  ];

  const historyData = (profile.history || []).map((h, i) => ({
    name: `S${i + 1}`,
    accuracy: h.accuracy,
    speed: h.speed
  }));

  const diffColors = { easy: '#09090B', medium: '#09090B', hard: '#09090B' };
  const diffLevel = settings?.difficulty_level || 'medium';

  return (
    <div className="min-h-screen" data-testid="cognitive-profile-page">
      {/* Header */}
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss">
          <Link to="/dashboard" className="mono-data text-sm text-zinc-500 hover:text-[#09090B] flex items-center gap-2 mb-4" data-testid="back-to-dashboard">
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <p className="mono-data text-[#09090B] mb-1">Cognitive Profile</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="profile-heading">
                Your Brain Map
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="mono-data text-xs text-zinc-500">Adaptive Mode</span>
              <span className="px-3 py-1 text-white text-xs font-bold tracking-widest uppercase" style={{ background: diffColors[diffLevel] }} data-testid="difficulty-badge">
                {diffLevel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss">
          <div className="dashboard-grid">
            {[
              { label: 'Memory', value: `${profile.memory_score_avg}%`, icon: Brain, color: '' },
              { label: 'Recall', value: `${profile.recall_accuracy}%`, icon: Target, color: 'text-[#09090B]' },
              { label: 'Speed', value: `${profile.speed_score}%`, icon: Zap, color: '' },
              { label: 'Trend', value: `${profile.improvement_trend > 0 ? '+' : ''}${profile.improvement_trend}%`, icon: profile.improvement_trend >= 0 ? TrendingUp : TrendingDown, color: profile.improvement_trend >= 0 ? 'text-[#09090B]' : 'text-[#09090B]' }
            ].map(stat => (
              <div key={stat.label} className="grid-border-item" data-testid={`stat-${stat.label.toLowerCase()}`}>
                <div className="label-swiss mb-2 flex items-center gap-2">
                  <stat.icon className="w-3 h-3" /> {stat.label}
                </div>
                <div className={`text-3xl font-black tracking-tighter ${stat.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#09090B]">
            {/* Radar Chart */}
            <div className="p-6 lg:border-r border-b lg:border-b-0 border-[#09090B]" data-testid="radar-chart">
              <h3 className="mono-data text-sm mb-4">Cognitive Dimensions</h3>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E4E4E7" />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12, fontFamily: 'IBM Plex Sans' }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar name="Score" dataKey="score" stroke="#09090B" fill="#09090B" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Line Chart */}
            <div className="p-6" data-testid="line-chart">
              <h3 className="mono-data text-sm mb-4">Progress Over Time</h3>
              {historyData.length > 1 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={historyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="accuracy" stroke="#09090B" strokeWidth={2} dot={{ r: 4 }} name="Accuracy" />
                    <Line type="monotone" dataKey="speed" stroke="#09090B" strokeWidth={2} dot={{ r: 3 }} name="Speed" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-zinc-400 mono-data text-sm">
                  Complete more sessions to see progress chart
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Strengths & Weaknesses */}
      <div className="border-b border-[#09090B]">
        <div className="container-swiss py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#09090B]">
            <div className="p-6 border-r border-[#09090B]" data-testid="strong-areas">
              <h3 className="mono-data text-sm text-[#09090B] mb-4 flex items-center gap-2">
                <Target className="w-3 h-3" /> Strengths
              </h3>
              {profile.strong_areas?.length > 0 ? (
                <div className="space-y-2">
                  {profile.strong_areas.map(a => (
                    <div key={a} className="flex items-center gap-3 py-2 border-b border-zinc-200 last:border-0">
                      <div className="w-2 h-2 bg-[#09090B]" />
                      <span className="font-medium capitalize">{a}</span>
                      <span className="mono-data text-xs text-[#09090B] ml-auto">Strong</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-400 text-sm">Keep training to identify your strengths.</p>
              )}
            </div>
            <div className="p-6" data-testid="weak-areas">
              <h3 className="mono-data text-sm text-[#09090B] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-3 h-3" /> Focus Areas
              </h3>
              {profile.weak_areas?.length > 0 ? (
                <div className="space-y-2">
                  {profile.weak_areas.map(a => (
                    <div key={a} className="flex items-center gap-3 py-2 border-b border-zinc-200 last:border-0">
                      <div className="w-2 h-2 bg-[#09090B]" />
                      <span className="font-medium capitalize">{a}</span>
                      <span className="mono-data text-xs text-[#09090B] ml-auto">Needs Work</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-400 text-sm">No weak areas detected. Impressive!</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="container-swiss py-8" data-testid="recommendations-section">
          <h3 className="mono-data text-sm text-[#09090B] mb-4 flex items-center gap-2">
            <BarChart3 className="w-3 h-3" /> Personalized Recommendations
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec, i) => (
              <div key={i} className="border border-[#09090B] p-4 flex items-center justify-between" data-testid={`recommendation-${i}`}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 ${rec.priority === 'high' ? 'bg-[#09090B]' : rec.priority === 'medium' ? 'bg-[#09090B]' : 'bg-zinc-400'}`} />
                    <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{rec.title}</span>
                    <span className="mono-data text-xs text-zinc-400">{rec.type}</span>
                  </div>
                  <p className="text-sm text-zinc-600">{rec.message}</p>
                </div>
                {rec.session_id && (
                  <Link to={`/session/${rec.session_id}`} className="btn-secondary text-xs px-4 py-2 whitespace-nowrap" data-testid={`rec-start-${i}`}>
                    Start
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
